/**
 * The prototype's render entry points, ported — `m04-engine.js` §credentialPlan/credentialSVG and
 * `m08-admin.js` §sampleData/previewSVG.
 *
 * Both return an SVG string, exactly as the reference's functions do, so a screen renders one with
 * `dangerouslySetInnerHTML` and gets the reference's own markup. The engine itself is in
 * `render/engine.ts`; this file is the thin layer that decides which layout, which data and which
 * options a credential or a template thumbnail is drawn with.
 *
 * The two things that make a credential's image reproducible: it is always drawn from its OWN
 * release snapshot and its OWN `renderDataSnapshot`, never from the mutable template, and the plan
 * is resolved strictly, so a record whose asset digest no longer matches draws the "could not be
 * verified" placard rather than a quietly different certificate.
 */
import constants from '../data/demo-constants.json';
import type { DemoCredential, DemoTemplate } from '../data/types';
import {
  credentialViewState,
  credTitle,
  currentRelease,
  displayName,
  isLocalizedDateField,
  localizedDateText,
  trUpper,
} from './reference';
import {
  canvasH,
  canvasW,
  esc,
  type Layout,
  type OutputSpec,
  planToSVG,
  type RenderData,
  type RenderPlan,
  resolveRenderPlan,
  type SVGOptions,
} from './render/engine';
import { assetById } from './render/library';

export * from './render/engine';
export { allAssets, allFonts, assetById, resolveAssetRef } from './render/library';
export { qrMatrix } from './render/qrcode';

// --------------------------------------------------------------- credential type vocabulary

interface CredentialTypeRecord {
  key: string;
  tr: string;
  en: string;
  nounTr: string;
  nounEn: string;
  defTr: string;
  defEn: string;
}

const CREDENTIAL_TYPES = constants.CREDENTIAL_TYPES as CredentialTypeRecord[];

/** Unknown keys fall back to the first record, as upstream — never to a thrown error. */
export const typeRec = (k: string | undefined): CredentialTypeRecord =>
  CREDENTIAL_TYPES.find((x) => x.key === k) || (CREDENTIAL_TYPES[0] as CredentialTypeRecord);

/** `trCap` capitalises through the Turkish rule, so "üyelik" becomes "Üyelik" and "iptal" "İptal". */
const trCap = (s: string): string => (s ? trUpper(s.slice(0, 1)) + s.slice(1) : s);
const enCap = (s: string): string => (s ? (s[0] as string).toUpperCase() + s.slice(1) : s);

export interface Terms {
  noun: string;
  nounDef: string;
  verified: string;
  expired: string;
  revoked: string;
  pending: string;
  pendingAdmin: string;
  ready: string;
  claimCta: string;
  download: string;
  holder: string;
  about: string;
  claimTitle: string;
  watermark: string;
}

/** `m02-mockdb.js` — every sentence the public and claim surfaces build around the type's noun. */
export function buildTerms(
  typeKey: string | undefined,
  customLabel: string | undefined,
  lang: 'tr' | 'en',
): Terms {
  const rec = typeRec(typeKey);
  const en = lang === 'en';
  let noun = en ? rec.nounEn : rec.nounTr;
  let def = en ? rec.defEn : rec.defTr;
  if (typeKey === 'custom' && customLabel) {
    noun = customLabel;
    def = customLabel;
  }
  const cap = en ? enCap : trCap;
  return en
    ? {
        noun,
        nounDef: def,
        verified: `Verified ${def}`,
        expired: `Expired ${def}`,
        revoked: `Revoked ${def}`,
        pending: `${cap(def)} awaiting acceptance`,
        pendingAdmin: `${cap(def)} awaiting publication`,
        ready: `Your ${noun} record is ready.`,
        claimCta: `Accept and publish my ${noun}`,
        download: `Download ${noun} image (PNG)`,
        holder: 'Awarded to',
        about: `What is this ${noun}?`,
        claimTitle: `Your ${noun} is ready — review and publish`,
        watermark: 'REVOKED / İPTAL EDİLDİ',
      }
    : {
        noun,
        nounDef: def,
        verified: `Doğrulanmış ${def}`,
        expired: `Süresi dolmuş ${def}`,
        revoked: `İptal edilmiş ${def}`,
        pending: `Onay bekleyen ${def}`,
        pendingAdmin: `Yayın onayı bekleyen ${def}`,
        ready: `${cap(noun)} kaydınız hazır.`,
        claimCta: `${cap(noun)} kaydımı onayla ve yayınla`,
        download: `${cap(noun)} görselini indir (PNG)`,
        holder: 'Sahibi',
        about: `Bu ${noun} nedir?`,
        claimTitle: `${cap(noun)} kaydınız hazır — kontrol edin ve yayınlayın`,
        watermark: 'İPTAL EDİLDİ / REVOKED',
      };
}

const releaseOf = (c: DemoCredential): Layout => c.release as unknown as Layout;

export const credData = (c: DemoCredential): RenderData => c.renderDataSnapshot as RenderData;

export const credTerms = (c: DemoCredential, lang: 'tr' | 'en'): Terms =>
  buildTerms(
    (c.release as { credentialType?: string }).credentialType,
    (c.release as { customTypeLabel?: string }).customTypeLabel,
    lang,
  );

// ------------------------------------------------------------------------------ credential SVG

export interface CredentialSVGOptions extends SVGOptions {
  lang?: 'tr' | 'en' | undefined;
  noWatermark?: boolean | undefined;
  output?: OutputSpec | undefined;
  strict?: boolean | undefined;
}

/** A credential's image is always its own release and its own render data — never the template's. */
export function credentialPlan(c: DemoCredential, opts: CredentialSVGOptions = {}): RenderPlan {
  const manifestOutput = (c.renderManifest as { output?: OutputSpec }).output ?? {};
  return resolveRenderPlan(
    releaseOf(c),
    credData(c),
    { ...manifestOutput, ...opts.output },
    {
      strict: opts.strict !== false,
    },
  );
}

export function credentialSVG(c: DemoCredential, opts: CredentialSVGOptions = {}): string {
  const plan = credentialPlan(c, opts);
  const T = credTerms(c, opts.lang || 'tr');
  const bgAsset = plan.background ? assetById(plan.background.assetId) : null;
  /* An opaque background paints the whole canvas, so the white underlay would be invisible; one
     with alpha needs it, or the page's own colour shows through the artwork. */
  const white = !bgAsset?.hasAlpha;
  if (!plan.ok) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${plan.canvas.width}" height="${plan.canvas.height}" viewBox="0 0 ${plan.canvas.width} ${plan.canvas.height}" style="width:100%;height:auto;display:block"><rect width="100%" height="100%" fill="#F6F4F1"/><text x="50%" y="50%" text-anchor="middle" font-family="Poppins,sans-serif" font-size="${Math.round(plan.canvas.width * 0.035)}" fill="#CF051E">Görsel doğrulanamadı</text></svg>`;
  }
  const svgOpts: SVGOptions = {
    responsive: true,
    white,
    attrs: `role="img" aria-label="${esc(credTitle(c, opts.lang || 'tr'))} – ${esc(displayName(c))}"`,
    ...(credentialViewState(c) === 'revoked' && !opts.noWatermark ? { watermark: T.watermark } : {}),
    ...opts,
  };
  return planToSVG(plan, svgOpts);
}

// ------------------------------------------------------------------------- template thumbnails

/**
 * The seed's `settings.publicBase`. The sample QR has to point somewhere and the reference reads
 * `DB.settings.publicBase`; a screen holding the live settings should pass its own value through
 * `publicBase` rather than rely on this.
 */
export const DEMO_PUBLIC_BASE = 'https://credentials.happyplacetowork.com.tr';

/** The fixed code the reference stamps on every preview, so two previews never differ by it. */
const SAMPLE_CODE = 'HPW-DEMO-KOD0-0000-0000-0000';

export type SampleMode = 'short' | 'long' | 'org';

export interface TemplateThumbOptions {
  lang?: 'tr' | 'en' | undefined;
  /** `short` is what the template grid draws; the preview modal switches to `long` to show shrink-to-fit. */
  mode?: SampleMode | undefined;
  publicBase?: string | undefined;
}

/** `layoutOf` — the published release when there is one, the draft otherwise. Preview only. */
export const layoutOf = (t: DemoTemplate): Layout => (currentRelease(t) ?? t.draft) as unknown as Layout;

/** `holderFieldKey` — which field carries the name the credential is made out to. */
export const holderFieldKey = (lay: Layout): string =>
  lay.holderField || (lay.subjectType === 'organization' ? 'organization_name' : 'recipient_name');

/**
 * `m08-admin.js` §sampleData. Every field gets its sample, its default or its own label, and the
 * holder field is overwritten with one of three fixed names — the long one exists to show a
 * layer shrinking, so it must stay exactly this long.
 */
export function sampleData(
  t: DemoTemplate,
  mode: SampleMode,
  lay: Layout,
  opts: TemplateThumbOptions = {},
): RenderData {
  const lang = opts.lang === 'en' ? 'en' : 'tr';
  const d: RenderData = {};
  const hf = holderFieldKey(lay);
  for (const f of lay.fieldDefinitions || []) {
    const value =
      f.sample || f.defaultValue || (f.labels ? (lang === 'en' ? f.labels.en : f.labels.tr) : null) || f.key;
    /*
     * A date is DRAWN in the language of the drawing — for the fields the SERVER draws that way.
     *
     * The field catalogue's own sample for «Veriliş Tarihi» is the words «2 Eylül 2026», and every
     * template built from it carries them, so a preview in English used to draw the Turkish date. The
     * value is read back to the day it names and written in this language's words; a value that is not
     * a date is drawn exactly as it stands (`localizedDateText`).
     *
     * `isLocalizedDateField` and not `dataType === 'date'`: the server formats `issue_date` and
     * `expiry_date` and hands every other field its claim verbatim, so a template's own date field
     * localized here would be a preview promising something the sealed document does not say.
     */
    d[f.key] = isLocalizedDateField(f.key) ? localizedDateText(value, lang) : value;
  }
  d.credential_title = lay.localizedMetadata?.[lang]?.title || t.name;
  const org = lay.subjectType === 'organization';
  d[hf] =
    mode === 'long'
      ? org
        ? 'Uluslararası Anadolu Lojistik ve Dağıtım Hizmetleri A.Ş.'
        : 'Alexandra Konstantinopoulou-Wetherington'
      : mode === 'org'
        ? 'ABC Holding'
        : org
          ? 'ABC Holding'
          : 'Ayşe Yılmaz';
  d.credential_code = SAMPLE_CODE;
  d.verification_url = `${opts.publicBase ?? DEMO_PUBLIC_BASE}/c/${SAMPLE_CODE}`;
  return d;
}

/** `m08-admin.js` §previewSVG — the editor and the template grid draw through this one path. */
export function previewSVG(layout: Layout, data: RenderData, opts: SVGOptions = {}): string {
  const plan = resolveRenderPlan(layout, data, {
    width: canvasW(layout.canvas),
    height: canvasH(layout.canvas),
  });
  return planToSVG(plan, { responsive: true, white: true, ...opts });
}

/** The thumbnail a template card shows: the layout drawn with sample values, never real ones. */
export function templateThumbSVG(t: DemoTemplate, opts: TemplateThumbOptions = {}): string {
  const lay = layoutOf(t);
  return previewSVG(lay, sampleData(t, opts.mode ?? 'short', lay, opts));
}

/**
 * The artwork a PUBLIC surface shows for a record.
 *
 * Binding rule 10: what a visitor sees has to be the artifact the render manifest was hashed over,
 * produced server-side and content-addressed — not a drawing this tab reproduced from the design.
 * So when the page has been handed the stored artifact (`/c/:code/artifacts/svg`, or the claim
 * link's token-gated equivalent), that is what it renders.
 *
 * The visual acceptance demo has no server and is handed nothing, so it falls through to the
 * client renderer and the parity capture keeps measuring the same bytes it always did.
 */
export function credentialArtwork(c: DemoCredential, options: CredentialSVGOptions = {}): string {
  const served = servedArtifact(c, options.lang);
  if (served !== null) return served;
  return credentialSVG(c, options);
}

/**
 * The STORED document for one language, or null when this record has none in it.
 *
 * `artifactSvgByLocale` is present only where the loader fetched per language; a record that has
 * only `artifactSvg` answers with it for its own language and with nothing for the other, which is
 * what lets a page say «no English version» instead of showing the Turkish one.
 */
export function servedArtifact(c: DemoCredential, lang?: 'tr' | 'en'): string | null {
  const byLocale = (c as { artifactSvgByLocale?: Record<string, unknown> }).artifactSvgByLocale;
  if (byLocale && lang) {
    const own = byLocale[lang];
    return typeof own === 'string' && own.length > 0 ? own : null;
  }
  const served = (c as { artifactSvg?: unknown }).artifactSvg;
  return typeof served === 'string' && served.length > 0 ? served : null;
}

/**
 * Whether this record is sealed in the language asked for at all.
 *
 * The release says which languages it seals; a release that says nothing seals one, which is every
 * release published before the TR/EN round — so this answers `true` for them and nothing changes.
 */
export function sealedInLanguage(c: DemoCredential, lang: 'tr' | 'en'): boolean {
  const sealed = c.release.sealedLanguages;
  if (!sealed || sealed.length === 0) return true;
  return sealed.includes(lang);
}
